import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import oscarsPartyService from '../services/oscarsPartyService';
import { useBeta } from './BetaContext';

const SettingsContext = createContext(null);

/**
 * Check if the submission deadline has passed
 * @param {string|Date|null} deadline - The submission deadline
 * @returns {boolean} - True if submissions are locked (deadline passed)
 */
const checkIfSubmissionLocked = (deadline) => {
  if (!deadline) return false;
  const deadlineDate = new Date(deadline);
  return Date.now() > deadlineDate.getTime();
};

/**
 * Calculate time remaining until deadline
 * @param {string|Date|null} deadline - The submission deadline
 * @returns {object|null} - Object with days, hours, minutes, seconds or null if no deadline
 */
const calculateTimeUntilDeadline = (deadline) => {
  if (!deadline) return null;

  const deadlineDate = new Date(deadline);
  const now = Date.now();
  const diff = deadlineDate.getTime() - now;

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0, isPast: true };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, totalMs: diff, isPast: false };
};

// Default party date: Sunday, March 15, 2026 at 5pm PST
// PST is UTC-8, so 5pm PST = 1am UTC on March 16
const DEFAULT_PARTY_DATE = '2026-03-16T00:00:00Z'; // 5pm PST on March 15, 2026

// Minimal cache system
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
let cache = {
  data: null,
  timestamp: null,
  partyId: null
};

/**
 * Get cached data if valid, otherwise return null
 */
const getCachedData = (partyId) => {
  if (cache.data && cache.partyId === partyId && cache.timestamp) {
    const age = Date.now() - cache.timestamp;
    if (age < CACHE_DURATION) {
      return cache.data;
    }
  }
  return null;
};

/**
 * Set cache data
 */
const setCachedData = (partyId, data) => {
  cache = {
    data,
    timestamp: Date.now(),
    partyId
  };
};

export const SettingsProvider = ({ children }) => {
  const { activePartyId } = useBeta();
  const [settings, setSettings] = useState({
    partyName: 'Oscars Party 2026',
    partyDate: DEFAULT_PARTY_DATE,
    description: '',
    ballotSubmissionDeadline: null,
    dateEnded: null
  });
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every second when close to deadline, every minute otherwise
  useEffect(() => {
    if (!settings.ballotSubmissionDeadline) return;

    const timeUntil = calculateTimeUntilDeadline(settings.ballotSubmissionDeadline);

    // If deadline passed, no need to update
    if (timeUntil?.isPast) return;

    // Update every second if within 1 hour, otherwise every minute
    const interval = timeUntil && timeUntil.totalMs < 60 * 60 * 1000 ? 1000 : 60000;

    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, interval);

    return () => clearInterval(timer);
  }, [settings.ballotSubmissionDeadline, currentTime]);

  useEffect(() => {
    if (!activePartyId) {
      setLoading(false);
      return;
    }

    // Check cache first
    const cachedData = getCachedData(activePartyId);
    if (cachedData) {
      setSettings(cachedData);
      setLoading(false);
      return;
    }

    // Fetch from Firestore using the service
    const fetchOscarsParty = async () => {
      try {
        const transformed = await oscarsPartyService.fetchOscarsPartyById(activePartyId);

        if (transformed) {
          // Map Firestore document fields to settings
          const mappedSettings = {
            partyName: `Martin-Resnick Oscar Party ${transformed.year || 2025}`,
            shortPartyName: `MR Oscar Party ${transformed.year || 2025}`,
            partyStartDate: transformed.dateStart
              ? new Date(transformed.dateStart).toISOString()
              : DEFAULT_PARTY_DATE,
            description: '',
            ballotSubmissionDeadline: transformed.dateBallotSubmissionDeadline || null,
            dateEnded: transformed.dateEnded || null,
            year: transformed.year,
            ballotEntryAmount: transformed.ballotEntryAmount,
            totalBallots: transformed.totalBallots,
            dateCreated: transformed.dateCreated,
            dateUpdated: transformed.dateUpdated
          };

          // Cache the data
          setCachedData(activePartyId, mappedSettings);
          setSettings(mappedSettings);
        } else {
          // Document doesn't exist, use defaults
          const defaultSettings = {
            partyName: 'Oscars Party 2026',
            shortPartyName: 'MR Oscar Party 2026',
            partyStartDate: DEFAULT_PARTY_DATE,
            description: '',
            ballotSubmissionDeadline: null,
            dateEnded: null
          };
          setSettings(defaultSettings);
        }
      } catch (error) {
        console.error('Error fetching Oscars party:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOscarsParty();
  }, [activePartyId]);

  // Compute submission lock status based on deadline
  const isSubmissionLocked = useMemo(() => {
    return checkIfSubmissionLocked(settings.ballotSubmissionDeadline);
  }, [settings.ballotSubmissionDeadline]);

  // Compute time remaining until deadline
  const timeUntilDeadline = useMemo(() => {
    return calculateTimeUntilDeadline(settings.ballotSubmissionDeadline);
  }, [settings.ballotSubmissionDeadline]);

  // Format deadline for display
  const formattedDeadline = useMemo(() => {
    if (!settings.partyStartDate) return null;
    return new Date(settings.partyStartDate).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/Los_Angeles',
      timeZoneName: 'short'
    });
  }, [settings.partyStartDate]);

  const value = {
    settings,
    loading,
    isSubmissionLocked,
    timeUntilDeadline,
    formattedDeadline
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
